import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingCart, Ruler, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useCart } from "@/context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;

const DetailSection = ({ title, text, items }) => (
  <div>
    {title && <h4 className="font-sub text-sm font-bold uppercase tracking-wide text-white">{title}</h4>}
    {text && <p className={`font-body text-[13px] leading-relaxed text-slate-400 ${title ? "mt-2" : ""}`}>{text}</p>}
    {items && (
      <ul className={`space-y-1.5 ${title || text ? "mt-2" : ""}`}>
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 font-body text-[13px] leading-relaxed text-slate-400">
            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-neon-pink" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const ProductModal = ({ product, onClose }) => {
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [chartOpen, setChartOpen] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const touchX = useRef(null);
  const { addItem, setOpen: setCartOpen } = useCart();

  useEffect(() => setImgIdx(0), [product?.id]);

  if (!product) return null;
  const gallery = [...new Set([product.image, ...(product.images || [])].filter(Boolean))];
  const currentImg = gallery[Math.min(imgIdx, gallery.length - 1)] || product.image;
  const flip = (dir) => setImgIdx((i) => (i + dir + gallery.length) % gallery.length);
  const hasSizes = Array.isArray(product.sizes) && product.sizes.length > 0;
  const total = (product.priceValue * qty).toFixed(2);
  const sizeSoldOut = (s) => {
    const stock = product.stock || {};
    return (product.track || s in stock) && Number(stock[s] ?? 0) <= 0;
  };
  const stockVals = Object.values(product.stock || {});
  const allSoldOut = stockVals.length > 0 && stockVals.every((v) => Number(v) <= 0);

  const requireSize = () => {
    if (allSoldOut) {
      toast.error("Sold Out — this item is currently sold out.");
      return false;
    }
    if (hasSizes && !size) {
      toast.error("Please select a size first.");
      return false;
    }
    if (hasSizes && size && sizeSoldOut(size)) {
      toast.error("That size is sold out.");
      return false;
    }
    return true;
  };

  const buildItem = () => ({
    product_id: product.id,
    name: product.name,
    size: size || null,
    quantity: qty,
    priceValue: product.priceValue,
    price: product.price,
    image: product.image,
  });

  const addToCart = () => {
    if (!requireSize()) return;
    addItem(buildItem());
    toast.success("Added to cart", {
      description: `${product.name}${size ? ` · Size ${size}` : ""} · Qty ${qty}`,
    });
    onClose();
  };

  const buyNow = () => {
    if (!requireSize()) return;
    addItem(buildItem());
    onClose();
    setCartOpen(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto overflow-x-hidden bg-void/85 p-3 py-6 backdrop-blur-md sm:items-center sm:p-4 sm:py-10"
        onClick={onClose}
        data-testid="product-modal"
      >
        <motion.div
          initial={{ scale: 0.94, y: 24 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.94, y: 24 }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          onClick={(e) => e.stopPropagation()}
          className="relative grid w-full max-w-5xl overflow-hidden rounded-3xl border border-neon-violet/40 bg-surface glow-ring lg:grid-cols-2"
        >
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-20 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-neon-pink"
            data-testid="product-modal-close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Image gallery */}
          <div
            className="relative bg-black"
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchX.current == null || gallery.length < 2) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              if (Math.abs(dx) > 40) flip(dx < 0 ? 1 : -1);
              touchX.current = null;
            }}
          >
            <img src={currentImg} alt={product.name} className="h-56 w-full object-cover sm:h-72 lg:h-full" data-testid="product-image" />
            {gallery.length > 1 && (
              <>
                <button onClick={() => flip(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-neon-pink" data-testid="gallery-prev" aria-label="Previous photo">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button onClick={() => flip(1)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-neon-pink" data-testid="gallery-next" aria-label="Next photo">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                  {gallery.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setImgIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${i === Math.min(imgIdx, gallery.length - 1) ? "w-5 bg-neon-cyan" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                      data-testid={`gallery-dot-${i}`}
                      aria-label={`Photo ${i + 1}`}
                    />
                  ))}
                </div>
                <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2 py-0.5 font-mono-cap text-[9px] text-white/80" data-testid="gallery-counter">
                  {Math.min(imgIdx, gallery.length - 1) + 1}/{gallery.length}
                </span>
              </>
            )}
            {product.badge && (
              <span className="absolute left-4 top-4 rounded-full bg-neon-pink px-3 py-1 font-mono-cap text-[9px] text-white">
                {product.badge}
              </span>
            )}
          </div>

          {/* Details (scrollable) */}
          <div className="max-h-[80vh] min-w-0 overflow-y-auto overflow-x-hidden p-5 sm:p-8">
            <p className="font-mono-cap text-[10px] text-neon-cyan">{product.category || "Merchandise"}</p>
            <h3 className="mt-2 break-words font-display text-xl leading-snug text-white sm:text-3xl sm:leading-tight">{product.name}</h3>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-display text-2xl gradient-text sm:text-3xl">{product.price}</span>
              {product.was && <span className="font-body text-slate-500 line-through">{product.was}</span>}
            </div>
            <p className="mt-3 font-body text-[13px] text-slate-400">{product.desc}</p>

            {product.details && (
              <div className="mt-6 space-y-5 border-t border-white/10 pt-6">
                {(product.details.sections || [
                  { title: "Package Includes", items: product.details.packageIncludes },
                  { title: "Product Details", items: product.details.productDetails },
                  { title: "Key Highlights", items: product.details.keyHighlights },
                  { title: "Important Notes", items: product.details.importantNotes },
                ]).map((s, i) => (
                  <DetailSection key={i} title={s.title} text={s.text} items={s.items} />
                ))}
                {product.details.notice && (
                  <div className="rounded-xl border border-neon-cyan/30 bg-neon-cyan/5 p-4">
                    <h4 className="font-sub text-sm font-bold uppercase tracking-wide text-neon-cyan">Important Notice</h4>
                    <p className="mt-2 font-body text-[12px] leading-relaxed text-slate-400">{product.details.notice}</p>
                  </div>
                )}
                {product.details.returnPolicy && (
                  <div className="rounded-xl border border-neon-pink/30 bg-neon-pink/5 p-4">
                    <h4 className="font-sub text-sm font-bold uppercase tracking-wide text-neon-pink">Return & Refund</h4>
                    <p className="mt-2 font-body text-[12px] leading-relaxed text-slate-400">{product.details.returnPolicy}</p>
                  </div>
                )}
              </div>
            )}

            {/* Size selector */}
            {hasSizes && (
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <span className="font-sub text-sm font-bold text-white">Jersey Size</span>
                  {product.sizeChart && (
                    <button
                      onClick={() => setChartOpen(true)}
                      className="flex items-center gap-1.5 font-mono-cap text-[10px] text-neon-cyan hover:underline"
                      data-testid="view-size-chart"
                    >
                      <Ruler className="h-3.5 w-3.5" /> Size Chart
                    </button>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.sizes.map((s) => {
                    const out = sizeSoldOut(s);
                    return (
                      <button
                        key={s}
                        onClick={() => !out && setSize(s)}
                        disabled={out}
                        className={`relative h-11 min-w-[3rem] rounded-full border px-4 font-sub text-sm font-semibold transition-all ${
                          out
                            ? "cursor-not-allowed border-white/10 text-slate-600 line-through"
                            : size === s
                            ? "border-neon-pink bg-neon-pink text-white"
                            : "border-white/20 text-slate-200 hover:border-neon-pink/60"
                        }`}
                        data-testid={`size-${s}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-6">
              <span className="font-sub text-sm font-bold text-white">Quantity</span>
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white hover:border-neon-pink"
                  data-testid="qty-minus"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-10 text-center font-display text-xl text-white" data-testid="qty-value">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white hover:border-neon-pink"
                  data-testid="qty-plus"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Total */}
            {allSoldOut && (
              <p className="mt-6 rounded-xl border border-neon-pink/40 bg-neon-pink/10 px-4 py-2.5 text-center font-mono-cap text-[10px] text-neon-pink" data-testid="modal-soldout">
                Sold Out — this item is currently sold out
              </p>
            )}
            <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
              <span className="font-mono-cap text-[11px] text-slate-400">Total</span>
              <span className="font-display text-2xl gradient-text" data-testid="product-total">
                MYR {total}
              </span>
            </div>

            {/* Actions */}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={addToCart}
                disabled={allSoldOut}
                className="flex items-center justify-center gap-2 rounded-full border border-neon-cyan/50 py-3.5 font-mono-cap text-[11px] text-white transition-colors hover:bg-neon-cyan/10 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="h-4 w-4" /> Add to Cart
              </button>
              <button
                onClick={buyNow}
                disabled={allSoldOut}
                className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet py-3.5 font-mono-cap text-[11px] text-white disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="buy-now-btn"
              >
                <Check className="h-4 w-4" /> Buy Now
              </button>
            </div>
          </div>
        </motion.div>

        {/* Size chart lightbox */}
        <AnimatePresence>
          {chartOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] flex items-center justify-center bg-void/90 p-4 backdrop-blur-md"
              onClick={(e) => {
                e.stopPropagation();
                setChartOpen(false);
              }}
              data-testid="size-chart-modal"
            >
              <button className="absolute right-5 top-5 rounded-full bg-black/50 p-2 text-white hover:bg-neon-pink">
                <X className="h-5 w-5" />
              </button>
              <img
                src={product.sizeChart}
                alt="Size chart"
                className="max-h-[88vh] w-auto rounded-2xl border border-neon-violet/40 object-contain glow-ring"
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};
